import { useState, useEffect, useCallback } from "react";
import { LuX, LuCheck } from "react-icons/lu";
import TransporterLayout from "../../components/TransporterLayout/TransporterLayout";
import WonBidDrawer from "../../components/WonBidDrawer/WonBidDrawer";
import { fetchMyBids, getTokenPayload } from "../../lib/directus";
import styles from "./TransporterMyBids.module.css";

function deriveStatus(bid, currentUserId) {
  const load = bid.load || {};
  const awardedContact = load.awarded_transporter?.contact;
  const biddingStatus  = (load.bidding_status || '').toLowerCase();
  const isClosed = !['open', 'pending', ''].includes(biddingStatus);
  if (isClosed && awardedContact) {
    return awardedContact === currentUserId ? 'Won' : 'Lost';
  }
  return bid.status || 'Pending';
}

function mapBid(bid, currentUserId) {
  const load  = bid.load || {};
  const batch = load.order?.batch || {};
  return {
    _id:              bid.id,
    _loadId:          load.id || null,
    loadId:           load.load_id ? `#${load.load_id}` : `#${String(load.id || '').slice(0, 8).toUpperCase()}`,
    pickup:           load.pickup_location || '-',
    delivery:         load.delivery_location || '-',
    cargo:            batch.commodity ? `${batch.commodity}${batch.grade ? ` (${batch.grade})` : ''}` : '-',
    commodity:        batch.commodity || '-',
    grade:            batch.grade || '-',
    quantity:         batch.quantity != null ? `${batch.quantity}${batch.unit_of_measure ? ' ' + batch.unit_of_measure : ' MT'}` : '-',
    bid:              bid.bid_amount ? `R${parseFloat(bid.bid_amount).toLocaleString()}` : '-',
    status:           deriveStatus(bid, currentUserId),
    assignBy:         load.delivery_deadline
                        ? new Date(load.delivery_deadline).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })
                        : '-',
    pickupDate:       load.pickup_date
                        ? new Date(load.pickup_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '-',
    deliveryDeadline: load.delivery_deadline
                        ? new Date(load.delivery_deadline).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '-',
    distance:         '-',
    estimatedTime:    '-',
    pickupWindow:     '-',
    seller:           '-',
    sellerRating:     0,
    sellerTrades:     0,
  };
}

function SuccessModal({ message, onClose }) {
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose}>
          <LuX size={18} />
        </button>
        <div className={styles.successIcon}>
          <LuCheck size={32} color="white" />
        </div>
        <p className={styles.successMsg}>{message}</p>
      </div>
    </div>
  );
}

export default function TransporterMyBids() {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All bids");
  const [selectedBid, setSelectedBid] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const loadData = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    const payload = getTokenPayload(token);
    const currentUserId = payload?.id || payload?.sub || null;
    fetchMyBids()
      .then((data) => setBids(data.map((b) => mapBid(b, currentUserId))))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const counts = {
    Pending: bids.filter((b) => b.status === "Pending").length,
    Won: bids.filter((b) => b.status === "Won").length,
    Lost: bids.filter((b) => b.status === "Lost").length,
  };

  const TABS = [
    { label: "All bids", filter: null },
    { label: `Pending${counts.Pending ? `(${counts.Pending})` : ''}`, filter: "Pending" },
    { label: `Won${counts.Won ? `(${counts.Won})` : ''}`, filter: "Won" },
    { label: `Lost${counts.Lost ? `(${counts.Lost})` : ''}`, filter: "Lost" },
  ];

  const currentFilter = TABS.find((t) => t.label === activeTab)?.filter;
  const filtered = currentFilter
    ? bids.filter((b) => b.status === currentFilter)
    : bids;

  function handleCardClick(bid) {
    if (bid.status === "Won") {
      setSelectedBid(bid);
    }
  }

  return (
    <TransporterLayout>
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>My bids</h1>

        {/* Tabs */}
        <div className={styles.tabsRow}>
          {TABS.map((tab) => (
            <button
              key={tab.label}
              className={`${styles.tab} ${activeTab === tab.label ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(tab.label)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Bids grid */}
        {loading ? (
          <p style={{ color: 'var(--secondary-text)', padding: '2rem' }}>Loading...</p>
        ) : filtered.length === 0 ? (
          <p style={{ color: 'var(--secondary-text)', padding: '2rem' }}>No bids found.</p>
        ) : (
          <div className={styles.cardsGrid}>
            {filtered.map((bid) => (
              <div
                key={bid._id}
                className={`${styles.bidCard} ${bid.status === "Won" ? styles.bidCardClickable : ""}`}
                onClick={() => handleCardClick(bid)}
              >
                {/* Top row */}
                <div className={styles.cardHeader}>
                  <div className={styles.cardTop}>
                    <span className={styles.loadIdText}>{bid.loadId}</span>
                    <span
                      className={
                        bid.status === "Won"
                          ? styles.badgeWon
                          : bid.status === "Pending"
                            ? styles.badgePending
                            : styles.badgeLost
                      }
                    >
                      {bid.status}
                    </span>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Route</span>
                    <span className={styles.infoValue}>
                      {bid.pickup} → {bid.delivery}
                    </span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Cargo</span>
                    <span className={styles.infoValue}>{bid.cargo}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Your bid</span>
                    <span className={styles.oliveValue}>{bid.bid}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <WonBidDrawer
        bid={selectedBid}
        onClose={() => setSelectedBid(null)}
        onAssigned={(truck) => {
          setSelectedBid(null);
          setSuccessMsg(`Delivery assigned to ${truck.name}!`);
          setShowSuccess(true);
          loadData();
        }}
      />

      {showSuccess && (
        <SuccessModal
          message={successMsg}
          onClose={() => setShowSuccess(false)}
        />
      )}
    </TransporterLayout>
  );
}
